import { useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';

/**
 * Webview-safe confirmation (Danny, 2026-10-01). Native `window.confirm`
 * is unreliable inside the Zoom client webview — it can be auto-dismissed
 * as accepted, so a "are you sure?" guard silently passes and an outbound
 * or destructive action fires on a single click (Scott's stipulation went
 * out "by itself"). This renders a real in-app modal instead, so the guard
 * always appears and must be answered.
 *
 * Drop-in for the `if (!window.confirm(msg)) return;` pattern:
 *   if (!(await confirmDialog(msg))) return;
 */
export interface ConfirmOptions {
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button for irreversible / outbound actions. */
  danger?: boolean;
}

export function confirmDialog(message: string, opts: ConfirmOptions = {}): Promise<boolean> {
  return new Promise((resolve) => {
    const host = document.createElement('div');
    document.body.appendChild(host);
    const root = createRoot(host);
    let settled = false;
    const finish = (value: boolean) => {
      if (settled) return;
      settled = true;
      resolve(value);
      // Defer teardown so the originating click/keydown finishes first.
      setTimeout(() => {
        root.unmount();
        host.remove();
      }, 0);
    };
    root.render(<ConfirmUI message={message} opts={opts} onResolve={finish} />);
  });
}

function ConfirmUI({
  message,
  opts,
  onResolve,
}: {
  message: string;
  opts: ConfirmOptions;
  onResolve: (v: boolean) => void;
}) {
  const confirmRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    confirmRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onResolve(false);
      else if (e.key === 'Enter') onResolve(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onResolve]);

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onClick={() => onResolve(false)}
    >
      <div className="w-full max-w-sm rounded-lg bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        {opts.title && <div className="mb-2 text-sm font-semibold text-gray-900">{opts.title}</div>}
        <div className="whitespace-pre-line text-sm leading-relaxed text-gray-700">{message}</div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            className="rounded-md border border-surface-border px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            onClick={() => onResolve(false)}
          >
            {opts.cancelLabel ?? 'Cancel'}
          </button>
          <button
            ref={confirmRef}
            type="button"
            className={
              'rounded-md px-3 py-1.5 text-sm font-medium text-white ' +
              (opts.danger ? 'bg-red-600 hover:bg-red-700' : 'bg-brand hover:opacity-90')
            }
            onClick={() => onResolve(true)}
          >
            {opts.confirmLabel ?? 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}
